/**
 * Indicator plugins — each is a self-contained plugin that attaches to
 * a ChartEngine, requests a lineSeries capability, and (re)computes on
 * every data/update notification.
 */
import { ema, rsi, vwap } from './math';
import { bollinger, doubleBollinger, keltner, supertrend } from './mathB';

function makeIndicatorPlugin({ name, compute, color, lineWidth = 1, constrainedToPriceBand = false, bandPct = 0.10 }) {
  return {
    name,
    _handle: null,
    _engine: null,
    attach(engine) {
      this._engine = engine;
      this._handle = engine.capability('lineSeries', {
        color, lineWidth,
        lastValueVisible: true,
        // TradingView-style gating: overlays that live on the price
        // scale (EMA/VWAP/SuperTrend/BB) opt in so they don't blow
        // out the axis when they drift far from price (Phase 52.1 G2).
        constrainedToPriceBand,
        bandPct,
      });
    },
    onData(candles) {
      if (!this._handle) return;
      this._handle.setData(compute(candles));
    },
    onUpdate(_last, candles) {
      // Fast path — recompute the FULL series (O(n), n≤1000, ~1 ms)
      // but only push the LAST row down to lightweight-charts via
      // `.update()`. lightweight-charts treats an update() as an
      // in-place patch of the terminal bar, avoiding a full line
      // rebuild every tick. Result: EMA / VWAP / SuperTrend ticks are
      // pixel-perfect and visually silky (Phase 52.1 G2 B.3).
      if (!this._handle) return;
      const rows = compute(candles);
      if (!rows.length) return;
      this._handle.update(rows[rows.length - 1]);
    },
    detach() {
      this._engine?.releaseCapability(this._handle);
      this._handle = null;
      this._engine = null;
    },
  };
}

/**
 * makeMultiSeriesPlugin — indicator that owns multiple line series
 * (e.g. Bollinger Bands has upper/middle/lower).
 * seriesSpecs: [{ key, color, lineWidth }] — key must match keys in
 * compute()'s output object.
 */
function makeMultiSeriesPlugin({ name, compute, seriesSpecs, constrainedToPriceBand = false, bandPct = 0.10 }) {
  return {
    name,
    _handles: {},
    _engine: null,
    attach(engine) {
      this._engine = engine;
      for (const spec of seriesSpecs) {
        this._handles[spec.key] = engine.capability('lineSeries', {
          color: spec.color, lineWidth: spec.lineWidth || 1, lastValueVisible: false,
          constrainedToPriceBand, bandPct,
        });
      }
    },
    onData(candles) {
      const result = compute(candles);
      for (const spec of seriesSpecs) {
        const rows = result[spec.key] || [];
        this._handles[spec.key]?.setData(rows);
      }
    },
    onUpdate(_last, candles) {
      // Same fast-path as makeIndicatorPlugin — only patch the last
      // row of each sub-series (Phase 52.1 G2 B.3).
      const result = compute(candles);
      for (const spec of seriesSpecs) {
        const rows = result[spec.key] || [];
        if (rows.length) this._handles[spec.key]?.update(rows[rows.length - 1]);
      }
    },
    detach() {
      for (const spec of seriesSpecs) {
        this._engine?.releaseCapability(this._handles[spec.key]);
      }
      this._handles = {};
      this._engine = null;
    },
  };
}

// Factory registry — new indicators just add a row here.
// NOTE: EMAs opt OUT of the auto-scale gate: operators explicitly
// want to see them even when they lag price by 5-10% (that's the
// entire *point* of a long-period MA on high-timeframe charts).
// Envelope-style overlays (DBB / KC / BB / VWAP / SuperTrend) keep
// the gate because they hug price naturally and only misbehave when
// a wild spike whipsaws the axis.
const OVERLAY_CONF = { constrainedToPriceBand: true, bandPct: 0.10 };
const EMA_CONF = {};  // no gate — always contribute
export const INDICATORS = {
  // ── EMA family (Phase 52.A + B) — always contribute to axis ──
  ema9:   { label: 'EMA 9',   factory: () => makeIndicatorPlugin({ name: 'ema9',   compute: (c) => ema(c, 9),   color: '#00ff88', lineWidth: 1.5, ...EMA_CONF }) },
  ema20:  { label: 'EMA 20',  factory: () => makeIndicatorPlugin({ name: 'ema20',  compute: (c) => ema(c, 20),  color: '#00e5ff', lineWidth: 1.5, ...EMA_CONF }) },
  ema21:  { label: 'EMA 21',  factory: () => makeIndicatorPlugin({ name: 'ema21',  compute: (c) => ema(c, 21),  color: '#ff9500', lineWidth: 1.5, ...EMA_CONF }) },
  ema50:  { label: 'EMA 50',  factory: () => makeIndicatorPlugin({ name: 'ema50',  compute: (c) => ema(c, 50),  color: '#f7c948', lineWidth: 1.5, ...EMA_CONF }) },
  ema100: { label: 'EMA 100', factory: () => makeIndicatorPlugin({ name: 'ema100', compute: (c) => ema(c, 100), color: '#ff7ac6', lineWidth: 2,   ...EMA_CONF }) },
  ema200: { label: 'EMA 200', factory: () => makeIndicatorPlugin({ name: 'ema200', compute: (c) => ema(c, 200), color: '#a78bfa', lineWidth: 2,   ...EMA_CONF }) },
  ema300: { label: 'EMA 300', factory: () => makeIndicatorPlugin({ name: 'ema300', compute: (c) => ema(c, 300), color: '#60a5fa', lineWidth: 2,   ...EMA_CONF }) },
  ema500: { label: 'EMA 500', factory: () => makeIndicatorPlugin({ name: 'ema500', compute: (c) => ema(c, 500), color: '#fbbf24', lineWidth: 2.5, ...EMA_CONF }) },
  // ── VWAP ──
  vwap:   { label: 'VWAP',    factory: () => makeIndicatorPlugin({ name: 'vwap',   compute: (c) => vwap(c),     color: '#f472b6', lineWidth: 2,   ...OVERLAY_CONF }) },
  // ── Bollinger Bands (52.B) ──
  bb:     {
    label: 'BB (20,2)',
    factory: () => makeMultiSeriesPlugin({
      name: 'bb',
      compute: (c) => bollinger(c, 20, 2),
      seriesSpecs: [
        { key: 'upper', color: 'rgba(0,229,255,0.6)', lineWidth: 1 },
        { key: 'mid',   color: 'rgba(247,201,72,0.9)', lineWidth: 1 },
        { key: 'lower', color: 'rgba(0,229,255,0.6)', lineWidth: 1 },
      ],
      ...OVERLAY_CONF,
    }),
  },
  // ── Double Bollinger Bands (Phase 52.1 G3) — 1σ + 2σ nested ──
  dbb: {
    label: 'DBB (20,1/2)',
    factory: () => makeMultiSeriesPlugin({
      name: 'dbb',
      compute: (c) => doubleBollinger(c, 20, 1, 2),
      seriesSpecs: [
        // outer 2σ — trend zone (dimmer, wider colour)
        { key: 'upper2', color: 'rgba(167,139,250,0.55)', lineWidth: 1 },
        { key: 'lower2', color: 'rgba(167,139,250,0.55)', lineWidth: 1 },
        // inner 1σ — mean-reversion zone (crisp cyan)
        { key: 'upper1', color: 'rgba(0,229,255,0.85)',  lineWidth: 1 },
        { key: 'lower1', color: 'rgba(0,229,255,0.85)',  lineWidth: 1 },
        // mid = 20-SMA
        { key: 'mid',    color: 'rgba(247,201,72,0.9)',  lineWidth: 1 },
      ],
      ...OVERLAY_CONF,
    }),
  },
  // ── Keltner Channel (Phase 52.1 G3) — EMA20 ± 1.5×ATR10 ──
  kc: {
    label: 'KC (20,1.5)',
    factory: () => makeMultiSeriesPlugin({
      name: 'kc',
      compute: (c) => keltner(c, 20, 10, 1.5),
      seriesSpecs: [
        { key: 'upper', color: 'rgba(0,255,136,0.55)', lineWidth: 1 },
        { key: 'mid',   color: 'rgba(0,255,136,0.85)', lineWidth: 1 },
        { key: 'lower', color: 'rgba(0,255,136,0.55)', lineWidth: 1 },
      ],
      ...OVERLAY_CONF,
    }),
  },
  // ── SuperTrend (52.B) ──
  supertrend: {
    label: 'SuperTrend',
    factory: () => makeIndicatorPlugin({
      name: 'supertrend',
      compute: (c) => supertrend(c, 10, 3),
      color: '#00ff88', lineWidth: 2,
      ...OVERLAY_CONF,
    }),
  },
  // RSI + MACD + ATR + ADX live in separate panes (rendered by
  // dedicated canvas components), not as chart-overlay plugins.
};

export function makeIndicator(key, colorOverrides = null) {
  // For colour-customisable indicators, bypass the frozen factory
  // and build a fresh plugin with the operator's palette applied.
  if (colorOverrides) {
    if (key === 'dbb') return makeMultiSeriesPlugin({
      name: 'dbb',
      compute: (c) => doubleBollinger(c, 20, 1, 2),
      seriesSpecs: [
        { key: 'upper2', color: colorOverrides.dbb_upper2 || 'rgba(167,139,250,0.55)', lineWidth: 1 },
        { key: 'lower2', color: colorOverrides.dbb_lower2 || 'rgba(167,139,250,0.55)', lineWidth: 1 },
        { key: 'upper1', color: colorOverrides.dbb_upper1 || 'rgba(0,229,255,0.85)',  lineWidth: 1 },
        { key: 'lower1', color: colorOverrides.dbb_lower1 || 'rgba(0,229,255,0.85)',  lineWidth: 1 },
        { key: 'mid',    color: colorOverrides.dbb_mid    || 'rgba(247,201,72,0.9)',  lineWidth: 1 },
      ],
      ...OVERLAY_CONF,
    });
    if (key === 'kc') return makeMultiSeriesPlugin({
      name: 'kc',
      compute: (c) => keltner(c, 20, 10, 1.5),
      seriesSpecs: [
        { key: 'upper', color: colorOverrides.kc_upper || 'rgba(0,255,136,0.55)', lineWidth: 1 },
        { key: 'mid',   color: colorOverrides.kc_mid   || 'rgba(0,255,136,0.85)', lineWidth: 1 },
        { key: 'lower', color: colorOverrides.kc_lower || 'rgba(0,255,136,0.55)', lineWidth: 1 },
      ],
      ...OVERLAY_CONF,
    });
  }
  const spec = INDICATORS[key];
  if (!spec) return null;
  return spec.factory();
}
